import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * useNotifications — fetches notifications + cheer prompts, polls every 30s,
 * surfaces new milestones as Sonner toasts, exposes mark-read actions.
 */
export function useNotifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const lastSeenIds = useRef(new Set());
  const cheeredIds = useRef(new Set());

  const fetchNotifs = useCallback(async (announceNew = true) => {
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
      if (announceNew) {
        for (const n of data.notifications || []) {
          if (!n.read && !lastSeenIds.current.has(n.id)) {
            if (n.kind === "milestone") {
              toast.success(n.title, {
                description: n.body,
                icon: <Trophy size={16} className="text-[#F5C542]" />,
                duration: 8000,
              });
            }
          }
          lastSeenIds.current.add(n.id);
        }
      } else {
        for (const n of data.notifications || []) lastSeenIds.current.add(n.id);
      }
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[useNotifications] fetchNotifs failed", e);
      }
    }
  }, []);

  const fetchCheers = useCallback(async () => {
    try {
      const { data } = await api.get("/cheer-prompts/mine");
      for (const p of data.prompts || []) {
        if (cheeredIds.current.has(p.post_id)) continue;
        cheeredIds.current.add(p.post_id);
        toast(`Cheer for ${p.creator_name}`, {
          description: `Just hit ${p.label} — drop a reaction?`,
          icon: <PartyPopper size={16} className="text-[#F5C542]" />,
          duration: 12000,
          action: {
            label: "Cheer →",
            onClick: () => {
              api.post(`/cheer-prompts/${p.post_id}/dismiss`).catch(() => {});
              navigate(p.celebration_url);
            },
          },
          onDismiss: () => {
            api.post(`/cheer-prompts/${p.post_id}/dismiss`).catch(() => {});
          },
        });
      }
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[useNotifications] fetchCheers failed", e);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotifs(false);
    fetchCheers();
    const t = setInterval(() => {
      fetchNotifs(true);
      fetchCheers();
    }, 30000);
    return () => clearInterval(t);
  }, [fetchNotifs, fetchCheers]);

  const markOne = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      console.error("[useNotifications] markOne failed", e);
    }
  }, []);

  const markAll = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      console.error("[useNotifications] markAll failed", e);
    }
  }, []);

  return { items, unread, markOne, markAll };
}
